import { AnalyticsEvent, track } from '@bonusly/analytics';

import { ACTIVE_SEASON, getActiveSeasonConfig } from '../seasonal-config';
import { todayLocalDate } from './bloom-engine';

/**
 * Manual override for visual testing. Flip to `true` to render the debug
 * slider and disable the real bloom orchestration (`useSpringBloom` no-ops),
 * so scrubbing the slider isn't immediately overwritten by computed values.
 *
 * Keep `false` for normal development and ALWAYS for shipped builds — the
 * slider is also gated on `import.meta.env.DEV` so it can never reach prod,
 * but the orchestration short-circuit is what makes the toggle meaningful.
 */
export const BLOOM_DEBUG_MODE = false;

/**
 * Spring Theme Bloom Store
 *
 * Holds the bloom value (0–1) plus the small caches that drive its
 * computation: the latest activity count from the API and the date the bloom
 * was last recomputed. All three pieces are persisted to localStorage so
 * progress survives reloads.
 *
 * Bloom mutation flows through `setBloomProgress`. When called with a `trigger`
 * it emits a `BloomLevelChanged` analytics event; the dev debug slider calls
 * it without a trigger so manual scrubbing doesn't pollute prod metrics.
 *
 * Progress mapping:
 *   0.0 = 100% "few"
 *   0.5 = 100% "some"
 *   1.0 = 100% "all"
 *   Values in between crossfade smoothly via `getBloomOpacities`.
 */

const baseKey = getActiveSeasonConfig().storageKey;

const BLOOM_KEY = `${baseKey}:bloom`;
const ACTIVITY_KEY = `${baseKey}:activity`;
const COMPUTED_AT_KEY = `${baseKey}:bloom-computed-at`;

const EVENT_NAME = 'spring-bloom-change';

export interface ActivityCache {
	totalCount: number;
	fetchedAt: number;
	/**
	 * The enableDate this count was fetched against. If enableDate ever changes
	 * for a user (shouldn't, but defense in depth) the cache is invalidated.
	 */
	enableDate: string;
}

export type BloomTrigger = 'cache_init' | 'floor_tick' | 'activity_lift';

let bloomProgress = 0;
let activityCache: ActivityCache | null = null;
let lastComputedDate: string | null = null;

const load = () => {
	try {
		const storedBloom = localStorage.getItem(BLOOM_KEY);
		bloomProgress = storedBloom ? Math.max(0, Math.min(1, parseFloat(storedBloom) || 0)) : 0;
	} catch {
		bloomProgress = 0;
	}

	try {
		const storedActivity = localStorage.getItem(ACTIVITY_KEY);
		if (storedActivity) {
			const parsed = JSON.parse(storedActivity) as Partial<ActivityCache>;
			if (typeof parsed.totalCount === 'number' && typeof parsed.fetchedAt === 'number' && typeof parsed.enableDate === 'string') {
				activityCache = { totalCount: parsed.totalCount, fetchedAt: parsed.fetchedAt, enableDate: parsed.enableDate };
			}
		}
	} catch {
		activityCache = null;
	}

	try {
		const storedComputed = localStorage.getItem(COMPUTED_AT_KEY);
		lastComputedDate = storedComputed ? JSON.parse(storedComputed) : null;
	} catch {
		lastComputedDate = null;
	}
};

const persistBloom = () => {
	try {
		localStorage.setItem(BLOOM_KEY, String(bloomProgress));
	} catch {
		// localStorage unavailable — best effort
	}
};

const persistActivity = () => {
	try {
		if (activityCache) {
			localStorage.setItem(ACTIVITY_KEY, JSON.stringify(activityCache));
		} else {
			localStorage.removeItem(ACTIVITY_KEY);
		}
	} catch {
		// best effort
	}
};

const persistComputedAt = () => {
	try {
		if (lastComputedDate) {
			localStorage.setItem(COMPUTED_AT_KEY, JSON.stringify(lastComputedDate));
		} else {
			localStorage.removeItem(COMPUTED_AT_KEY);
		}
	} catch {
		// best effort
	}
};

const dispatch = () => window.dispatchEvent(new CustomEvent(EVENT_NAME));

load();

export const getBloomProgress = (): number => bloomProgress;

export const getActivityCache = (): ActivityCache | null => activityCache;

export const getLastComputedDate = (): string | null => lastComputedDate;

/**
 * Returns opacity values (0–1) for each landscape variant based on current
 * progress. Crossfades between adjacent stages so the transition is smooth.
 */
export const getBloomOpacities = (progress = bloomProgress): { few: number; some: number; all: number } => {
	if (progress <= 0) return { few: 1, some: 0, all: 0 };
	if (progress >= 1) return { few: 0, some: 0, all: 1 };

	if (progress <= 0.5) {
		// Crossfade few → some
		const t = progress / 0.5;
		return { few: 1 - t, some: t, all: 0 };
	}

	// Crossfade some → all
	const t = (progress - 0.5) / 0.5;
	return { few: 0, some: 1 - t, all: t };
};

interface SetBloomOptions {
	/**
	 * Why bloom is changing. Required for production callers — emitting
	 * `BloomLevelChanged` analytics is gated on its presence so the debug
	 * slider doesn't pollute metrics.
	 */
	trigger?: BloomTrigger;
	/** Activity total at the moment of the change. Included on the event. */
	totalCount?: number;
}

/**
 * Update bloom and (optionally) emit analytics. Production callers MUST pass
 * a `trigger`; doing so enforces the never-regress guarantee and emits a
 * `BloomLevelChanged` event. The debug slider calls without a trigger so it
 * can scrub freely (including downward) without polluting analytics.
 */
export const setBloomProgress = (p: number, options: SetBloomOptions = {}) => {
	const next = Math.max(0, Math.min(1, p));
	const previous = bloomProgress;

	if (options.trigger) {
		if (next <= previous) return;

		bloomProgress = next;
		persistBloom();
		dispatch();

		track(AnalyticsEvent.SeasonalTheme.BloomLevelChanged, {
			season: ACTIVE_SEASON,
			previous_bloom: previous,
			new_bloom: bloomProgress,
			trigger: options.trigger,
			total_count: options.totalCount ?? activityCache?.totalCount ?? 0,
		});
		return;
	}

	if (next === previous) return;

	bloomProgress = next;
	persistBloom();
	dispatch();
};

export const setActivityCache = (cache: ActivityCache) => {
	activityCache = cache;
	persistActivity();
};

export const clearActivityCache = () => {
	activityCache = null;
	persistActivity();
};

export const setLastComputedDate = (date = todayLocalDate()) => {
	lastComputedDate = date;
	persistComputedAt();
};

export const onBloomChange = (callback: () => void) => {
	window.addEventListener(EVENT_NAME, callback);
	return () => window.removeEventListener(EVENT_NAME, callback);
};

if (import.meta.env.DEV) {
	const win = window as unknown as Record<string, unknown>;
	if (!win.__spring) win.__spring = {};
	const api = win.__spring as Record<string, unknown>;

	Object.defineProperties(api, {
		bloomProgress: { get: () => bloomProgress, configurable: true },
		activityCache: { get: () => activityCache, configurable: true },
		lastComputedDate: { get: () => lastComputedDate, configurable: true },
	});

	api.setBloomProgress = (p: number) => {
		setBloomProgress(p);
		console.log(`[spring] bloom progress → ${(bloomProgress * 100).toFixed(0)}%`);
	};
	api.resetBloom = () => {
		setBloomProgress(0);
		console.log('[spring] bloom progress reset to 0%');
	};
	api.clearActivityCache = () => {
		clearActivityCache();
		console.log('[spring] activity cache cleared');
	};
}
